import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { globalFeatureFlags } from '@/lib/featureFlags';
import { logger } from '@/lib/logger';

interface MutationConfig<TData, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: any, variables: TVariables) => void;
  successMessage?: string;
  errorMessage?: string;
  invalidateQueries?: string[][];
  
  // ✅ NEW: Optimistic update configuration (Phase 2)
  optimisticUpdate?: {
    enabled: boolean;
    updater: (oldData: any, variables: TVariables) => any;
    rollbackOnError?: boolean; // default: true
  };
  optimisticQueryKeys?: string[][];
}

export const useMutationWithToast = <TData, TVariables>({
  mutationFn,
  onSuccess,
  onError,
  successMessage,
  errorMessage,
  invalidateQueries = [],
  optimisticUpdate,
  optimisticQueryKeys
}: MutationConfig<TData, TVariables>) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // ✅ SAFE: Only enable optimistic updates if flag ON
  const isOptimisticEnabled = globalFeatureFlags.isEnabled('ENABLE_API_OPTIMISTIC_UPDATES');

  return useMutation({
    mutationFn,
    
    // ✅ Optimistic update (only if flag ON and enabled in config)
    onMutate: isOptimisticEnabled && optimisticUpdate?.enabled 
      ? async (variables) => {
          try {
            // Cancel outgoing refetches
            await Promise.all(
              (optimisticQueryKeys || []).map(key => 
                queryClient.cancelQueries({ queryKey: key })
              )
            );
            
            // Snapshot previous values
            const previousData = (optimisticQueryKeys || []).map(key => ({
              key,
              data: queryClient.getQueryData(key)
            }));
            
            // Optimistically update
            (optimisticQueryKeys || []).forEach(key => {
              queryClient.setQueryData(key, (old: any) => {
                if (!old) return old;
                return optimisticUpdate.updater(old, variables);
              });
            });
            
            return { previousData };
          } catch (error) {
            logger.error('[Optimistic Update] Failed to apply optimistic update:', error);
            return undefined;
          }
        }
      : undefined,
      
    onSuccess: (data, variables) => {
      // Invalidate queries
      invalidateQueries.forEach(queryKey => {
        queryClient.invalidateQueries({ queryKey });
      });

      // Show success toast
      if (successMessage) {
        toast({
          title: "Thành công",
          description: successMessage
        });
      }

      // Custom success handler
      onSuccess?.(data, variables);
    },
    
    onError: (error: any, variables, context) => {
      // ✅ Rollback if flag ON and rollbackOnError !== false
      if (isOptimisticEnabled && 
          optimisticUpdate?.rollbackOnError !== false && 
          context?.previousData) {
        context.previousData.forEach(({ key, data }: any) => {
          queryClient.setQueryData(key, data);
        });
        
        if (import.meta.env.DEV) {
          console.warn('[Optimistic Update] Rolled back due to error:', error);
        }
      }
      
      // Show error toast
      toast({
        title: "Lỗi",
        description: error.message || errorMessage || "Có lỗi xảy ra",
        variant: "destructive"
      });

      // Custom error handler
      onError?.(error, variables);
    }
  });
};